import React, { useMemo, useState } from "react";
import axios from "axios";
import {
  ArrowUpRight,
  Building2,
  Users,
  Compass,
  Globe2,
  Mail,
  Phone,
  MapPin,
  CheckCircle2,
} from "lucide-react";
import SectionHeading from "../components/SectionHeading";
import Turnstile from "../components/Turnstile";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  SITE,
  HERO_IMAGE,
  ABOUT_PORTRAIT,
  HOME_HIGHLIGHTS,
  TIMELINE,
  SERVICES,
  PROJECTS,
  EXPERIENCE_SECTIONS,
  MEMBERSHIPS,
  MILESTONES,
  ENQUIRY_TYPES,
  ABOUT_BIO,
  ABOUT_STATS,
} from "../lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
const ICONS = [Building2, Users, Compass, Globe2];

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - 80;
  window.scrollTo({ top, behavior: "smooth" });
};

const initialForm = {
  full_name: "",
  company: "",
  email: "",
  mobile: "",
  enquiry_type: "",
  message: "",
};

const Field = ({ label, name, value, onChange, type = "text", required = true, testId }) => (
  <label className="block">
    <span className="label-small text-grey-soft">
      {label}
      {required && <span className="text-gold ml-1">*</span>}
    </span>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      data-testid={testId}
      className="mt-3 w-full bg-transparent border-b border-ink-border focus:border-gold outline-none py-3 text-ivory text-base placeholder-grey-deep transition-colors"
      autoComplete="off"
    />
  </label>
);

const Landing = () => {
  // Projects filter
  const tags = useMemo(() => {
    const set = new Set(PROJECTS.map((p) => p.tag));
    return ["All Projects", ...Array.from(set)];
  }, []);
  const [activeTag, setActiveTag] = useState("All Projects");
  const filteredProjects = activeTag === "All Projects" ? PROJECTS : PROJECTS.filter((p) => p.tag === activeTag);

  // Contact form
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState({ state: "idle", message: "" });
  const [captchaToken, setCaptchaToken] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.enquiry_type) {
      setStatus({ state: "error", message: "Please select a type of enquiry." });
      return;
    }
    if (TURNSTILE_SITE_KEY && !captchaToken) {
      setStatus({ state: "error", message: "Please complete the captcha to continue." });
      return;
    }
    setStatus({ state: "loading", message: "" });

    // Step 1: backend verifies the captcha and stores the enquiry.
    try {
      await axios.post(
        `${API}/contact`,
        { ...form, captcha_token: captchaToken },
        { headers: { "Content-Type": "application/json" } }
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const rawDetail =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail) && detail[0]?.msg
            ? `${detail[0].loc?.slice(-1)[0] || "Field"}: ${detail[0].msg}`
            : null;
      const isCaptchaErr = typeof rawDetail === "string" && /captcha/i.test(rawDetail);
      setStatus({
        state: "error",
        message: isCaptchaErr
          ? "Captcha expired or already used. The verification has been refreshed — please tick the checkbox once more and resubmit."
          : rawDetail || "We could not submit your enquiry. Please refresh the page and try again.",
      });
      if (window.turnstile) {
        try { window.turnstile.reset(); } catch (_) { /* noop */ }
      }
      setCaptchaToken("");
      return;
    }

    // Step 2: browser delivers the email via Web3Forms (free-plan compatible).
    try {
      const body = {
        access_key: process.env.REACT_APP_WEB3FORMS_KEY || "",
        subject: `New Advisory Enquiry: ${form.full_name} (${form.enquiry_type})`,
        from_name: "Kevin Tan Website",
        reply_to: form.email,
        name: form.full_name,
        email: form.email,
        company: form.company,
        mobile: form.mobile,
        enquiry_type: form.enquiry_type,
        message: form.message,
        botcheck: "",
      };
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!data?.success) {
        throw new Error(data?.message || "Email delivery failed.");
      }
      setStatus({
        state: "success",
        message: "Thank you. Your enquiry has been received and Kevin Tan will be in touch soon.",
      });
      setForm(initialForm);
      setCaptchaToken("");
      if (window.turnstile) {
        try { window.turnstile.reset(); } catch (_) { /* noop */ }
      }
    } catch (err) {
      setStatus({
        state: "error",
        message: err?.message || "We could not submit your enquiry. Please try again or email Kevin directly.",
      });
    }
  };

  return (
    <main data-testid="landing-page" className="animate-fade-in">
      {/* ============ HERO ============ */}
      <section
        id="hero"
        data-testid="hero-section"
        className="relative min-h-[100vh] flex items-end overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/85 via-ink/70 to-ink" aria-hidden />
        <div className="absolute inset-0 grain" aria-hidden />

        <div className="container-editorial relative z-10 pb-20 md:pb-28 pt-40 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-9">
            <div className="eyebrow mb-6" data-testid="hero-eyebrow">
              Shopping Mall &amp; Retail Advisory, Malaysia
            </div>
            <h1
              data-testid="hero-headline"
              className="font-serif text-[2.6rem] sm:text-5xl md:text-6xl lg:text-[4.4rem] leading-[1.02] text-ivory tracking-tight text-balance max-w-5xl"
            >
              Shaping Retail Destinations With{" "}
              <span className="italic text-gold">Experience</span>, Strategy and Commercial Clarity.
            </h1>
            <p
              data-testid="hero-subheadline"
              className="mt-8 max-w-2xl text-grey-soft text-base md:text-lg leading-relaxed"
            >
              With over {SITE.yearsExperience} years of experience across Malaysia’s shopping mall and retail
              property landscape, Kevin Tan advises developers, landlords and investors on how to plan,
              reposition and operate commercially resilient retail destinations.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <button
                type="button"
                onClick={() => scrollToId("contact")}
                data-testid="hero-cta-engage"
                className="inline-flex items-center justify-center gap-3 px-9 py-4 bg-gold text-ink font-medium tracking-[0.18em] text-[0.78rem] uppercase hover:bg-gold-hover transition-colors duration-300"
              >
                Engage Kevin Tan <ArrowUpRight size={16} />
              </button>
              <button
                type="button"
                onClick={() => scrollToId("services")}
                data-testid="hero-cta-services"
                className="inline-flex items-center justify-center gap-3 px-9 py-4 border border-gold text-ivory font-medium tracking-[0.18em] text-[0.78rem] uppercase hover:bg-gold/10 transition-colors duration-300"
              >
                View Advisory Services
              </button>
            </div>
          </div>

          <div className="lg:col-span-3 hidden lg:block">
            <div className="flex flex-col items-end gap-4 text-right">
              <div className="label-small text-grey-soft">Est. Practice</div>
              <div className="font-serif text-6xl text-gold leading-none">
                38<span className="text-ivory">+</span>
              </div>
              <div className="label-small text-ivory/70">Years in retail property</div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ HIGHLIGHTS ============ */}
      <section data-testid="highlights-section" className="container-editorial py-24 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-ink-border">
          {HOME_HIGHLIGHTS.map((h, i) => {
            const Icon = ICONS[i % ICONS.length];
            return (
              <div
                key={h.title}
                data-testid={`highlight-card-${i}`}
                className="bg-ink p-10 md:p-12 group hover:bg-ink-soft transition-colors duration-500"
              >
                <Icon size={22} className="text-gold mb-8" strokeWidth={1.25} />
                <div className="label-small text-gold mb-3">{h.eyebrow}</div>
                <h3 className="font-serif text-xl md:text-2xl text-ivory leading-snug">{h.title}</h3>
                <p className="mt-4 text-grey-soft text-sm leading-relaxed">{h.copy}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ============ STRATEGIC ADVISORY ============ */}
      <section className="border-y border-ink-border bg-ink-soft">
        <div className="container-editorial py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Practice"
              title={<>Strategic Retail Property <span className="italic text-gold">Advisory</span></>}
            />
          </div>
          <div className="lg:col-span-7">
            <p className="text-ivory/85 text-lg md:text-xl leading-relaxed font-light">
              Kevin helps retail assets improve positioning, tenant mix, leasing strategy, customer experience
              and commercial performance. Decades of operating experience translate into clear, executable
              decisions.
            </p>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                "Positioning & destination strategy",
                "Tenant mix & leasing direction",
                "Customer journey & layout advisory",
                "Operational frameworks & KPIs",
              ].map((item, idx) => (
                <div
                  key={item}
                  data-testid={`advisory-pillar-${idx}`}
                  className="flex items-start gap-4 border-t border-ink-border pt-5"
                >
                  <span className="font-serif text-gold text-sm leading-none mt-1">0{idx + 1}</span>
                  <p className="text-ivory/90 text-base leading-relaxed">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ FOR DEVELOPERS / LANDLORDS ============ */}
      <section className="container-editorial py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Engagements"
              title={<>For Developers, Landlords &amp; Mixed-Use <span className="italic text-gold">Destinations</span></>}
              copy="Advisory engagements span new developments, existing malls, underperforming assets, integrated developments, and retail components within commercial and mixed-use properties."
            />
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              { title: "New Developments", copy: "Concept, masterplan, feasibility and tenant strategy from day one." },
              { title: "Existing Malls", copy: "Strategic refresh, leasing reset and operational uplift." },
              { title: "Underperforming Assets", copy: "Diagnosis and repositioning into commercially resilient destinations." },
              { title: "Integrated Developments", copy: "Retail components within mixed-use, township and corporate schemes." },
            ].map((card, idx) => (
              <div
                key={card.title}
                data-testid={`audience-card-${idx}`}
                className="bg-ink-soft border border-ink-border p-8 md:p-10 hover:border-gold/50 transition-colors duration-500 group"
              >
                <div className="flex items-start justify-between mb-6">
                  <span className="label-small text-gold">0{idx + 1}</span>
                  <ArrowUpRight size={16} className="text-grey-deep group-hover:text-gold transition-colors" />
                </div>
                <h3 className="font-serif text-2xl text-ivory leading-snug">{card.title}</h3>
                <p className="mt-3 text-grey-soft text-sm leading-relaxed">{card.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ ABOUT ============ */}
      <section id="about" data-testid="about-section" className="border-t border-ink-border scroll-mt-24">
        <div className="container-editorial pt-24 md:pt-32 pb-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8">
              <div className="eyebrow mb-6">About Kevin Tan</div>
              <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl text-ivory leading-[1.05] tracking-tight text-balance">
                A Pioneer in Malaysia's <span className="italic text-gold">Shopping Mall</span> Industry
              </h2>
            </div>
            <div className="lg:col-span-4">
              <div className="space-y-3 lg:pl-10 lg:border-l border-ink-border">
                <p className="label-small text-gold">Profile</p>
                <p className="text-ivory text-base">{SITE.name}</p>
                <p className="text-grey-soft text-sm">{SITE.role}</p>
                <p className="text-grey-soft text-sm">{SITE.location}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="container-editorial pb-24 md:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="relative">
                <div
                  className="aspect-[4/5] bg-cover bg-center border border-ink-border"
                  style={{ backgroundImage: `url(${ABOUT_PORTRAIT})` }}
                  aria-hidden
                />
                <div className="absolute -bottom-6 -right-6 hidden md:flex flex-col items-end bg-ink-soft border border-gold/40 px-7 py-5">
                  <span className="font-serif text-4xl text-gold leading-none">38<span className="text-ivory">+</span></span>
                  <span className="label-small text-ivory/80 mt-2">Years of practice</span>
                </div>
              </div>
            </div>
            <div className="lg:col-span-7 order-1 lg:order-2 space-y-7">
              {ABOUT_BIO.map((para, idx) => (
                <p
                  key={idx}
                  data-testid={`about-bio-${idx}`}
                  className={
                    idx === 0
                      ? "text-ivory/90 text-lg md:text-xl leading-relaxed font-light"
                      : "text-grey-soft text-base md:text-lg leading-relaxed"
                  }
                >
                  {para}
                </p>
              ))}
              <div className="hairline my-2 max-w-sm" />
              <button
                type="button"
                onClick={() => scrollToId("contact")}
                data-testid="about-cta-engage"
                className="inline-flex items-center gap-3 px-8 py-4 border border-gold text-ivory text-[0.78rem] tracking-[0.2em] uppercase hover:bg-gold/10 transition-colors duration-300"
              >
                Engage Kevin
              </button>
            </div>
          </div>

          {/* Stats strip */}
          <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-px bg-ink-border border-y border-ink-border">
            {ABOUT_STATS.map((s, i) => (
              <div
                key={s.label}
                data-testid={`about-stat-${i}`}
                className="bg-ink p-8 md:p-10 flex flex-col"
              >
                <span className="font-serif text-4xl md:text-5xl text-gold leading-none">{s.value}</span>
                <span className="mt-4 label-small text-ivory/70 leading-snug">{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Timeline */}
        <div className="bg-ink-soft border-t border-ink-border">
          <div className="container-editorial py-24 md:py-32">
            <SectionHeading
              eyebrow="Career Path"
              title={<>A career across Malaysia's defining <span className="italic text-gold">retail destinations</span>.</>}
            />
            <div className="mt-16 relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-ink-border md:-translate-x-1/2" aria-hidden />
              <ol className="space-y-12 md:space-y-16">
                {TIMELINE.map((item, idx) => {
                  const left = idx % 2 === 0;
                  return (
                    <li
                      key={item.org}
                      data-testid={`timeline-item-${idx}`}
                      className="relative md:grid md:grid-cols-2 md:gap-12 pl-12 md:pl-0"
                    >
                      <span
                        className="absolute left-4 md:left-1/2 top-2 w-2.5 h-2.5 bg-gold md:-translate-x-1/2"
                        aria-hidden
                      />
                      <div className={left ? "md:text-right md:pr-12" : "md:col-start-2 md:pl-12"}>
                        <div className="label-small text-gold mb-2">{item.year}</div>
                        <h3 className="font-serif text-2xl md:text-3xl text-ivory leading-snug">{item.org}</h3>
                        <p className="mt-3 text-grey-soft text-sm md:text-base leading-relaxed max-w-md md:max-w-none md:inline-block">
                          {item.role}
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* ============ SERVICES ============ */}
      <section id="services" data-testid="services-section" className="border-t border-ink-border scroll-mt-24">
        <div className="container-editorial pt-24 md:pt-32 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8">
              <div className="eyebrow mb-6">Advisory Services</div>
              <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl text-ivory leading-[1.05] tracking-tight text-balance">
                Senior retail property advisory, grounded in <span className="italic text-gold">operating experience</span>.
              </h2>
            </div>
            <div className="lg:col-span-4">
              <p className="text-grey-soft text-base leading-relaxed lg:pl-10 lg:border-l border-ink-border">
                Engagements are scoped to a client’s asset, ambition and stage. Feasibility and concept
                through to repositioning, operations and long-term performance.
              </p>
            </div>
          </div>
        </div>

        <div className="container-editorial pb-24 md:pb-28">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-ink-border border border-ink-border">
            {SERVICES.map((s, idx) => (
              <article
                key={s.title}
                data-testid={`service-card-${idx}`}
                className="bg-ink p-10 md:p-12 lg:p-14 group hover:bg-ink-soft transition-colors duration-500"
              >
                <div className="flex items-start justify-between mb-10">
                  <span className="font-serif text-gold text-sm">
                    {String(idx + 1).padStart(2, "0")} / {String(SERVICES.length).padStart(2, "0")}
                  </span>
                  <ArrowUpRight size={16} className="text-grey-deep group-hover:text-gold transition-colors" />
                </div>
                <h3 className="font-serif text-2xl md:text-3xl text-ivory leading-snug">{s.title}</h3>
                <p className="mt-5 text-grey-soft text-sm md:text-base leading-relaxed max-w-md">{s.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ============ PROJECTS ============ */}
      <section id="projects" data-testid="projects-section" className="border-t border-ink-border bg-ink-soft scroll-mt-24">
        <div className="container-editorial pt-24 md:pt-32 pb-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8">
              <div className="eyebrow mb-6">Selected Projects</div>
              <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl text-ivory leading-[1.05] tracking-tight text-balance">
                Retail destinations shaped across <span className="italic text-gold">Malaysia &amp; the region</span>.
              </h2>
            </div>
            <div className="lg:col-span-4">
              <p className="text-grey-soft text-base leading-relaxed lg:pl-10 lg:border-l border-ink-border">
                A selection of malls, mixed-use developments and retail destinations Kevin has helped develop,
                reposition or advise.
              </p>
            </div>
          </div>
        </div>

        <div className="container-editorial pb-10">
          <div className="flex flex-wrap items-center gap-2 border-y border-ink-border py-5">
            {tags.map((tag) => (
              <button
                key={tag}
                type="button"
                data-testid={`project-filter-${tag.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setActiveTag(tag)}
                className={`px-4 py-2 text-[0.7rem] tracking-[0.22em] uppercase transition-colors duration-300 ${
                  activeTag === tag ? "bg-gold text-ink" : "text-ivory/70 hover:text-gold border border-ink-border"
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>

        <div className="container-editorial pb-24 md:pb-32">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-ink-border border border-ink-border">
            {filteredProjects.map((p, idx) => (
              <article
                key={p.name}
                data-testid={`project-card-${idx}`}
                className="bg-ink p-10 md:p-12 group hover:bg-ink-soft transition-colors duration-500 flex flex-col min-h-[300px]"
              >
                <div className="flex items-center justify-between mb-8">
                  <span className="label-small text-gold">{p.tag}</span>
                  <span className="font-serif text-grey-deep text-sm">{String(idx + 1).padStart(2, "0")}</span>
                </div>
                <h3 className="font-serif text-2xl md:text-[1.7rem] text-ivory leading-snug">{p.name}</h3>
                {p.location && (
                  <p className="mt-2 text-xs uppercase tracking-[0.22em] text-grey-deep">{p.location}</p>
                )}
                <p className="mt-5 text-grey-soft text-sm leading-relaxed flex-1">{p.role}</p>
                <div className="mt-8 h-px bg-ink-border group-hover:bg-gold/60 transition-colors" />
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ============ EXPERIENCE ============ */}
      <section id="experience" data-testid="experience-section" className="border-t border-ink-border scroll-mt-24">
        <div className="container-editorial pt-24 md:pt-32 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-9">
              <div className="eyebrow mb-6">Industry Experience</div>
              <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl text-ivory leading-[1.05] tracking-tight text-balance">
                Decades of Leadership Across <span className="italic text-gold">Retail, Property and Destination Management</span>.
              </h2>
            </div>
          </div>
        </div>

        <div className="container-editorial pb-24 md:pb-28">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-ink-border border border-ink-border">
            {EXPERIENCE_SECTIONS.map((s, idx) => (
              <div key={s.title} data-testid={`experience-section-${idx}`} className="bg-ink p-10 md:p-12">
                <span className="label-small text-gold">0{idx + 1}</span>
                <h3 className="mt-6 font-serif text-2xl md:text-3xl text-ivory leading-snug">{s.title}</h3>
                <p className="mt-5 text-grey-soft text-sm md:text-base leading-relaxed">{s.copy}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="border-y border-ink-border bg-ink-soft">
          <div className="container-editorial py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <SectionHeading
                eyebrow="Standing"
                title={<>Professional <span className="italic text-gold">Memberships</span></>}
                copy="Active involvement with leading industry bodies in shopping centre and property management."
              />
            </div>
            <div className="lg:col-span-7">
              <ul className="divide-y divide-ink-border border-y border-ink-border">
                {MEMBERSHIPS.map((m, idx) => (
                  <li key={m} data-testid={`membership-${idx}`} className="py-6 flex items-start gap-6">
                    <span className="font-serif text-gold text-sm leading-none mt-1 shrink-0">0{idx + 1}</span>
                    <span className="text-ivory/90 text-base md:text-lg leading-relaxed">{m}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="container-editorial py-24 md:py-32">
          <SectionHeading
            eyebrow="Achievements"
            title={<>Achievements &amp; <span className="italic text-gold">Milestones</span></>}
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-ink-border border border-ink-border">
            {MILESTONES.map((m, idx) => (
              <div key={m} data-testid={`milestone-${idx}`} className="bg-ink p-10 md:p-12 flex gap-6 items-start">
                <span className="font-serif text-gold text-3xl leading-none">
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <p className="text-ivory/85 text-base md:text-lg leading-relaxed">{m}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CONTACT ============ */}
      <section id="contact" data-testid="contact-section" className="border-t border-ink-border scroll-mt-24">
        <div className="container-editorial pt-24 md:pt-32 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8">
              <div className="eyebrow mb-6">Contact</div>
              <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl text-ivory leading-[1.05] tracking-tight text-balance">
                Let's Build Better <span className="italic text-gold">Retail Destinations</span>.
              </h2>
              <p className="mt-8 max-w-2xl text-grey-soft text-base md:text-lg leading-relaxed">
                Whether you are developing a new retail destination, repositioning an existing mall or exploring
                a retail component within a mixed-use project, Kevin Tan provides strategic advisory grounded in
                decades of hands-on industry leadership.
              </p>
            </div>
          </div>
        </div>

        <div className="container-editorial pb-24 md:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-ink-border border border-ink-border">
            <aside className="lg:col-span-5 bg-ink-soft p-10 md:p-14">
              <div className="eyebrow mb-6">Direct</div>
              <h3 className="font-serif text-3xl text-ivory leading-snug">{SITE.name}</h3>
              <p className="mt-1 text-grey-soft text-sm">{SITE.role}</p>
              <div className="hairline my-10 max-w-xs" />
              <ul className="space-y-6">
                <li className="flex items-start gap-4">
                  <Mail size={18} className="text-gold mt-1" strokeWidth={1.25} />
                  <div>
                    <div className="label-small text-grey-soft mb-1">Email</div>
                    <a
                      href={`mailto:${SITE.email}`}
                      data-testid="contact-email-link"
                      className="text-ivory hover:text-gold transition-colors text-base break-all"
                    >
                      {SITE.email}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <Phone size={18} className="text-gold mt-1" strokeWidth={1.25} />
                  <div>
                    <div className="label-small text-grey-soft mb-1">Mobile</div>
                    <a
                      href={`tel:${SITE.mobile.replace(/\s/g, "")}`}
                      data-testid="contact-phone-link"
                      className="text-ivory hover:text-gold transition-colors text-base"
                    >
                      {SITE.mobile}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <MapPin size={18} className="text-gold mt-1" strokeWidth={1.25} />
                  <div>
                    <div className="label-small text-grey-soft mb-1">Based In</div>
                    <span className="text-ivory text-base">{SITE.location}</span>
                  </div>
                </li>
              </ul>
              <div className="hairline my-10 max-w-xs" />
              <p className="text-grey-soft text-sm leading-relaxed">
                Advisory enquiries are reviewed personally. Please share a brief outline of your asset, location
                and intent so the response can be meaningful.
              </p>
            </aside>

            <div className="lg:col-span-7 bg-ink p-10 md:p-14">
              {status.state === "success" ? (
                <div
                  data-testid="contact-success"
                  className="min-h-[420px] flex flex-col items-start justify-center"
                >
                  <CheckCircle2 size={36} className="text-gold mb-8" strokeWidth={1.25} />
                  <div className="eyebrow mb-4">Enquiry Received</div>
                  <h3 className="font-serif text-3xl md:text-4xl text-ivory leading-snug max-w-lg text-balance">
                    Thank you. Your enquiry has been received and Kevin Tan will be in touch soon.
                  </h3>
                  <button
                    type="button"
                    onClick={() => setStatus({ state: "idle", message: "" })}
                    data-testid="contact-send-another"
                    className="mt-10 inline-flex items-center gap-3 px-8 py-4 border border-gold text-ivory text-[0.78rem] tracking-[0.2em] uppercase hover:bg-gold/10 transition-colors duration-300"
                  >
                    Send another enquiry
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-8" noValidate>
                  <div className="eyebrow">Enquiry Form</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
                    <Field label="Full Name" name="full_name" value={form.full_name} onChange={handleChange} testId="input-full-name" />
                    <Field label="Company" name="company" value={form.company} onChange={handleChange} required={false} testId="input-company" />
                    <Field label="Email Address" name="email" type="email" value={form.email} onChange={handleChange} testId="input-email" />
                    <Field label="Mobile Number" name="mobile" type="tel" value={form.mobile} onChange={handleChange} testId="input-mobile" />
                  </div>

                  <div>
                    <span className="label-small text-grey-soft">
                      Type of Enquiry<span className="text-gold ml-1">*</span>
                    </span>
                    <Select
                      value={form.enquiry_type}
                      onValueChange={(v) => setForm((f) => ({ ...f, enquiry_type: v }))}
                    >
                      <SelectTrigger
                        data-testid="select-enquiry-type"
                        className="mt-3 w-full rounded-none bg-transparent border-0 border-b border-ink-border focus:border-gold focus:ring-0 text-ivory py-6 text-base px-0 hover:border-gold/60"
                      >
                        <SelectValue placeholder="Select an enquiry type" />
                      </SelectTrigger>
                      <SelectContent className="bg-ink-soft border border-ink-border rounded-none">
                        {ENQUIRY_TYPES.map((t) => (
                          <SelectItem
                            key={t}
                            value={t}
                            data-testid={`enquiry-option-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            className="text-ivory focus:bg-gold/10 focus:text-gold rounded-none"
                          >
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <label className="block">
                    <span className="label-small text-grey-soft">
                      Message<span className="text-gold ml-1">*</span>
                    </span>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      rows={5}
                      required
                      data-testid="input-message"
                      className="mt-3 w-full bg-transparent border-b border-ink-border focus:border-gold outline-none py-3 text-ivory text-base placeholder-grey-deep resize-none transition-colors"
                      placeholder="Share a brief outline of your asset, location and intent."
                    />
                  </label>

                  {status.state === "error" && (
                    <p data-testid="contact-error" className="text-sm text-red-300/90 border-l-2 border-red-400/60 pl-4">
                      {status.message}
                    </p>
                  )}

                  {TURNSTILE_SITE_KEY && (
                    <div className="pt-2">
                      <span className="label-small text-grey-soft block mb-3">Verification</span>
                      <Turnstile
                        siteKey={TURNSTILE_SITE_KEY}
                        theme="dark"
                        onToken={(t) => setCaptchaToken(t)}
                        onExpire={() => setCaptchaToken("")}
                      />
                    </div>
                  )}

                  <div className="pt-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <button
                      type="submit"
                      disabled={status.state === "loading"}
                      data-testid="contact-submit"
                      className="inline-flex items-center gap-3 px-10 py-4 bg-gold text-ink font-medium tracking-[0.2em] text-[0.78rem] uppercase hover:bg-gold-hover transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {status.state === "loading" ? "Sending…" : "Send Enquiry"}
                      {status.state !== "loading" && <ArrowUpRight size={16} />}
                    </button>
                    <p className="text-[0.7rem] uppercase tracking-[0.22em] text-grey-deep">
                      Responses typically within 2–3 business days.
                    </p>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Landing;
